/**
 * notifications-section.tsx — Cài đặt cá nhân → Thông báo.
 *
 * "Phương thức thông báo" kiểu SaleSmartly: âm thanh (chọn âm, âm lượng, nghe
 * thử), thông báo trình duyệt, nhấp nháy tiêu đề tab, chu kỳ âm báo, tắt âm khi
 * Bận. Lưu theo người dùng trên máy chủ (PUT /notification-settings/me) nên đi
 * theo tài khoản sang máy khác; quyền thông báo trình duyệt thì theo từng trình duyệt.
 *
 * AI được báo tin nào do quản trị chọn ở Cài đặt → Thông báo tin nhắn.
 */
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Check, Play } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { i18n } from '@/lib/i18n'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/misc'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Loading } from '@/components/shared/feedback'
import { desktopPermission } from '@/components/shared/notification-center'
import { apiError } from '@/lib/api-client'
import { cn } from '@/lib/utils'
import { useMyNotifyPrefs, useSaveMyNotifyPrefs } from '@/hooks/use-notification-settings'
import {
  DEFAULT_USER_NOTIFY, NOTIFY_TONES, TONE_LABEL, type DesktopPermission, type NotifyTone, type UserNotifyPrefs,
} from '@/lib/notify/notify-decision'
import { playNotifySound, unlockNotifySound, useNotifySoundStore } from '@/lib/notify/notify-sound'
import { showSystemNotification } from '@/lib/notify/system-notification'
import { PUSH_NOTE, disablePush, enablePush, isPushSubscribed } from '@/lib/web-push'
import { AccountRow, AccountSection } from './account-ui'

const INTERVALS = [5, 10, 15, 30, 60]
const MAX_TIMES = [2, 3, 5, 10]

const PERMISSION_TEXT: Record<DesktopPermission, string> = {
  get granted() { return i18n.t('accountPages:notifications.permission.granted') },
  get default() { return i18n.t('accountPages:notifications.permission.default') },
  get denied() { return i18n.t('accountPages:notifications.permission.denied') },
  get unsupported() { return i18n.t('accountPages:notifications.permission.unsupported') },
}

export function NotificationsSection() {
  const { t } = useTranslation('accountPages')
  const { data, isLoading } = useMyNotifyPrefs()
  const save = useSaveMyNotifyPrefs()
  const prefs = data ?? DEFAULT_USER_NOTIFY
  const [perm, setPerm] = useState<DesktopPermission>(desktopPermission)
  const [volume, setVolume] = useState(prefs.sound.volume)
  const soundBlocked = useNotifySoundStore((s) => s.blocked)
  // Trạng thái đăng ký thông báo ĐẨY (cả khi đóng ứng dụng) của riêng thiết bị này.
  const [pushNote, setPushNote] = useState('')

  useEffect(() => { setVolume(prefs.sound.volume) }, [prefs.sound.volume])

  // Quyền có thể đổi ngoài trang này (hộp xin quyền ở góc, ổ khoá cạnh thanh địa chỉ) → đọc lại khi tuỳ chọn đổi / quay lại tab.
  useEffect(() => {
    const sync = () => setPerm(desktopPermission())
    sync()
    window.addEventListener('focus', sync)
    return () => window.removeEventListener('focus', sync)
  }, [prefs.desktop])

  useEffect(() => {
    if (!(prefs.desktop && perm === 'granted')) { setPushNote(''); return }
    void isPushSubscribed().then((on) => setPushNote(on ? PUSH_NOTE.ok : ''))
  }, [prefs.desktop, perm])

  // Âm lượng: kéo thanh trượt thì lưu sau khi dừng tay 400ms.
  useEffect(() => {
    if (volume === prefs.sound.volume) return
    const t = setTimeout(() => update({ sound: { ...prefs.sound, volume } }), 400)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [volume])

  if (isLoading && !data) return <Loading label={t('notifications.loading')} />

  function update(patch: Partial<UserNotifyPrefs>) {
    save.mutate(patch, { onError: (e) => toast.error(apiError(e)) })
  }

  async function preview(tone: NotifyTone = prefs.sound.tone) {
    await unlockNotifySound()
    const ok = await playNotifySound(tone, volume, true)
    if (!ok) toast.error(t('notifications.soundError'))
  }

  async function toggleDesktop(on: boolean) {
    if (!on) {
      update({ desktop: false })
      setPushNote('')
      void disablePush() // thiết bị này thôi nhận thông báo đẩy
      return
    }
    if (perm === 'unsupported') return toast.error(PERMISSION_TEXT.unsupported)
    let p: DesktopPermission = perm
    if (p === 'default') {
      try {
        p = (await Notification.requestPermission()) as DesktopPermission
      } catch {
        p = desktopPermission()
      }
      setPerm(p)
    }
    if (p !== 'granted') {
      toast.error(PERMISSION_TEXT.denied)
      return
    }
    update({ desktop: true })
    setPushNote(PUSH_NOTE[await enablePush()])
  }

  function testDesktop() {
    void showSystemNotification(t('notifications.testTitle'), {
      body: t('notifications.testBody'), tag: 'ecdp-test', url: '/account/notifications',
    })
  }

  const soundOff = !prefs.sound.enabled
  const repeatOn = prefs.repeat.mode === 'repeat'

  return (
    <div className="max-w-3xl space-y-6">
      <AccountSection
        title={t('notifications.sectionMethod')}
        description={t('notifications.sectionMethodDesc')}
      >
        <AccountRow label={t('notifications.soundLabel')} hint={t('notifications.soundHint')}>
          <div className="flex items-center gap-3">
            <Switch
              checked={prefs.sound.enabled}
              onCheckedChange={(v) => update({ sound: { ...prefs.sound, enabled: v } })}
              aria-label={t('notifications.soundLabel')}
            />
            {soundBlocked && prefs.sound.enabled && (
              <button type="button" onClick={() => void unlockNotifySound()} className="text-xs text-amber-700 underline-offset-2 hover:underline dark:text-amber-300">
                {t('notifications.soundBlocked')}
              </button>
            )}
          </div>
        </AccountRow>

        <AccountRow label={t('notifications.toneLabel')}>
          <div className={cn('flex flex-wrap items-center gap-2', soundOff && 'opacity-60')}>
            {NOTIFY_TONES.map((tone) => {
              const active = prefs.sound.tone === tone
              return (
                <button
                  key={tone}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  disabled={soundOff}
                  onClick={() => { update({ sound: { ...prefs.sound, tone } }); void preview(tone) }}
                  className={cn(
                    'flex h-8 items-center gap-1.5 rounded-md border px-3 text-sm transition-colors',
                    active ? 'border-primary bg-primary/5 text-primary' : 'hover:bg-accent',
                  )}
                >
                  {active && <Check className="h-3.5 w-3.5" />}
                  {TONE_LABEL[tone]}
                </button>
              )
            })}
            <Button variant="outline" size="sm" className="h-8" disabled={soundOff} onClick={() => void preview()}>
              <Play className="h-3.5 w-3.5" /> {t('notifications.testSound')}
            </Button>
          </div>
        </AccountRow>

        <AccountRow label={t('notifications.volumeLabel')}>
          <div className={cn('flex max-w-sm items-center gap-3', soundOff && 'opacity-60')}>
            <input
              type="range" min={0} max={100} step={5}
              value={volume}
              disabled={soundOff}
              onChange={(e) => setVolume(Number(e.target.value))}
              className="h-1.5 flex-1 cursor-pointer accent-primary"
              aria-label={t('notifications.volumeLabel')}
            />
            <span className="w-10 text-right text-sm tabular-nums text-muted-foreground">{volume}%</span>
          </div>
        </AccountRow>

        <AccountRow label={t('notifications.desktopLabel')} hint={PERMISSION_TEXT[perm]}>
          <div className="flex items-center gap-3">
            <Switch
              checked={prefs.desktop && perm === 'granted'}
              onCheckedChange={(v) => void toggleDesktop(v)}
              disabled={perm === 'unsupported'}
              aria-label={t('notifications.desktopLabel')}
            />
            {prefs.desktop && perm === 'granted' && (
              <Button variant="outline" size="sm" className="h-8" onClick={testDesktop}>{t('notifications.sendTest')}</Button>
            )}
          </div>
          <p className="mt-1.5 text-xs text-muted-foreground">
            {t('notifications.desktopHint')}
          </p>
          {pushNote && <p className="mt-1 text-xs text-muted-foreground">{pushNote}</p>}
        </AccountRow>

        <AccountRow label={t('notifications.titleFlashLabel')} hint={t('notifications.titleFlashHint')}>
          <Switch
            checked={prefs.titleFlash}
            onCheckedChange={(v) => update({ titleFlash: v })}
            aria-label={t('notifications.titleFlashLabel')}
          />
        </AccountRow>
      </AccountSection>

      <AccountSection title={t('notifications.sectionRepeat')}>
        <AccountRow label={t('notifications.repeatModeLabel')}>
          <div className={cn('flex flex-wrap gap-2', soundOff && 'opacity-60')} role="radiogroup" aria-label={t('notifications.sectionRepeat')}>
            {([['once', t('notifications.repeatOnce')], ['repeat', t('notifications.repeatUntilBack')]] as const).map(([id, label]) => (
              <button
                key={id}
                type="button"
                role="radio"
                aria-checked={prefs.repeat.mode === id}
                disabled={soundOff}
                onClick={() => update({ repeat: { ...prefs.repeat, mode: id } })}
                className={cn(
                  'h-8 rounded-md border px-3 text-sm transition-colors',
                  prefs.repeat.mode === id ? 'border-primary bg-primary/5 text-primary' : 'hover:bg-accent',
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </AccountRow>
        {repeatOn && (
          <AccountRow label={t('notifications.repeatLabel')} hint={t('notifications.repeatHint')}>
            <div className={cn('flex flex-wrap items-center gap-2 text-sm', soundOff && 'opacity-60')}>
              <span>{t('notifications.every')}</span>
              <Select
                value={String(prefs.repeat.intervalSec)}
                onValueChange={(v) => update({ repeat: { ...prefs.repeat, intervalSec: Number(v) } })}
                disabled={soundOff}
              >
                <SelectTrigger className="h-8 w-[100px]" aria-label={t('notifications.intervalLabel')}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[...new Set([...INTERVALS, prefs.repeat.intervalSec])].sort((a, b) => a - b).map((s) => (
                    <SelectItem key={s} value={String(s)}>{t('notifications.seconds', { count: s })}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span>{t('notifications.upTo')}</span>
              <Select
                value={String(prefs.repeat.maxTimes)}
                onValueChange={(v) => update({ repeat: { ...prefs.repeat, maxTimes: Number(v) } })}
                disabled={soundOff}
              >
                <SelectTrigger className="h-8 w-[90px]" aria-label={t('notifications.maxTimesLabel')}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[...new Set([...MAX_TIMES, prefs.repeat.maxTimes])].sort((a, b) => a - b).map((n) => (
                    <SelectItem key={n} value={String(n)}>{t('notifications.times', { count: n })}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </AccountRow>
        )}
        <AccountRow label={t('notifications.muteWhenBusyLabel')} hint={t('notifications.muteWhenBusyHint')}>
          <Switch
            checked={prefs.muteWhenBusy}
            onCheckedChange={(v) => update({ muteWhenBusy: v })}
            aria-label={t('notifications.muteWhenBusyLabel')}
          />
        </AccountRow>
      </AccountSection>
    </div>
  )
}
